/**
 * API Service Layer
 * Centralized API handling with automatic token injection and error handling.
 * Includes transparent token refresh on 401 responses.
 */

import axios from 'axios';
import { getEnv } from '@/utils/env';

// Token and logout callback - will be set from main.js after Pinia is initialized
let getToken = () => null;
let doLogout = () => {};
let doRefresh = () => Promise.resolve(false);

// Flag to prevent multiple concurrent refresh attempts from interceptors
let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Queue failed requests while a token refresh is in progress.
 * Once refresh completes, all queued requests are retried.
 */
function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

function onTokenRefreshFailed() {
  refreshSubscribers.forEach((cb) => cb(null));
  refreshSubscribers = [];
}

// Create axios instance with base configuration
const api = axios.create({
  baseURL: getEnv('VITE_VITRAIL_API'),
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000, // 30 seconds timeout
  withCredentials: true // Send HttpOnly cookies (refresh_token) with requests
});

// Request interceptor - automatically inject auth token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['x-access-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 with transparent token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 - Unauthorized (token expired or invalid)
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh or logout endpoints
      const url = originalRequest.url || '';
      if (url.includes('/api/token/refresh') || url.includes('/api/logout') || url.includes('/api/login')) {
        doLogout();
        return Promise.reject(error);
      }
      
      originalRequest._retry = true;
      
      if (isRefreshing) {
        // Another request is already refreshing — queue this one
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            if (newToken) {
              originalRequest.headers['x-access-token'] = newToken;
              resolve(api(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }
      
      isRefreshing = true;
      
      try {
        const success = await doRefresh();
        if (success) {
          const newToken = getToken();
          onTokenRefreshed(newToken);
          originalRequest.headers['x-access-token'] = newToken;
          return api(originalRequest);
        } else {
          onTokenRefreshFailed();
          doLogout();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        onTokenRefreshFailed();
        doLogout();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Initialize API service with auth store
 * Must be called after Pinia is initialized
 * @param {Function} tokenGetter - Function that returns the current token
 * @param {Function} logoutCallback - Function to call on logout
 * @param {Function} refreshCallback - Function to refresh the access token, returns Promise<boolean>
 */
export function initializeApiService(tokenGetter, logoutCallback, refreshCallback) {
  getToken = tokenGetter;
  doLogout = logoutCallback;
  if (refreshCallback) {
    doRefresh = refreshCallback;
  }
}

/**
 * Error messages in French for common HTTP status codes
 */
const ERROR_MESSAGES = {
  400: 'Requête invalide. Veuillez vérifier les données saisies.',
  401: 'Session expirée. Veuillez vous reconnecter.',
  403: 'Accès refusé. Vous n\'avez pas les permissions nécessaires.',
  404: 'Ressource introuvable.',
  409: 'Conflit. Cette ressource existe déjà.',
  422: 'Données invalides. Veuillez vérifier les informations.',
  429: 'Trop de requêtes. Veuillez patienter.',
  500: 'Erreur serveur. Veuillez réessayer plus tard.',
  502: 'Service temporairement indisponible.',
  503: 'Service en maintenance. Veuillez réessayer plus tard.',
  504: 'Le serveur met trop de temps à répondre.'
};

/**
 * Parse error response and return user-friendly message
 * @param {Error} error - Axios error object
 * @returns {Object} - { message: string, status: number|null, data: any }
 */
export function parseError(error) {
  // Network error - no response received
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return {
        message: 'La requête a expiré. Vérifiez votre connexion internet.',
        status: null,
        data: null
      };
    }
    return {
      message: 'Impossible de contacter le serveur. Vérifiez votre connexion internet.',
      status: null,
      data: null
    };
  }

  const { status, data } = error.response;
  
  // Try to get error message from response
  let message = data?.error || data?.message || data?.detail;
  
  // If no message in response, use default for status code
  if (!message) {
    message = ERROR_MESSAGES[status] || 'Une erreur inattendue est survenue.';
  }

  return {
    message,
    status,
    data
  };
}

/**
 * API Service object with helper methods
 */
const ApiService = {
  /**
   * GET request
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  get(endpoint, params = {}) {
    return api.get(endpoint, { params });
  },

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise}
   */
  post(endpoint, data = {}) {
    return api.post(endpoint, data);
  },

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise}
   */
  put(endpoint, data = {}) {
    return api.put(endpoint, data);
  },

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise}
   */
  patch(endpoint, data = {}) {
    return api.patch(endpoint, data);
  },

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise}
   */
  delete(endpoint) {
    return api.delete(endpoint);
  },

  /**
   * POST request with Basic Auth (for login)
   * @param {string} endpoint - API endpoint
   * @param {string} username - Username/email
   * @param {string} password - Password
   * @returns {Promise}
   */
  postWithBasicAuth(endpoint, username, password) {
    return api.post(endpoint, null, {
      auth: { username, password }
    });
  },

  /**
   * Upload file
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - Form data with file
   * @returns {Promise}
   */
  upload(endpoint, formData) {
    return api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  /**
   * Raw axios instance for advanced use cases (e.g., responseType: 'blob')
   */
  axios: api
};

// Export both the service and the raw api instance
export { api };
export default ApiService;
